import type {
  RecruitingJob,
  RecruitingJobLocationOption,
} from '@gokrew/models/recruiting';
import {
  compact,
  matchLocation,
} from '../recruiting/JobLocationFields/jobLocation';

/**
 * The company location a recruiting job is at: the one picked on the job
 * form (same city, province and postal code), else the only location in
 * that city and province. Undefined for a job with no city, a one-off
 * address, or a city with several locations.
 */
export const jobLocationId = (
  locations: RecruitingJobLocationOption[],
  job: Pick<RecruitingJob, 'city' | 'province' | 'postalCode'>
): string | undefined => {
  if (!job.city?.trim()) return undefined;
  const picked = matchLocation(locations, {
    city: job.city,
    province: job.province ?? '',
    postalCode: job.postalCode ?? '',
  });
  if (picked) return picked.id;
  const sameCity = locations.filter(
    (location) =>
      compact(location.city) === compact(job.city) &&
      (location.province ?? '') === (job.province ?? '')
  );
  return sameCity.length === 1 ? sameCity[0].id : undefined;
};

/**
 * The location the Add employee form starts on, among the ones the admin
 * may pick: the first of `preferred` they can (the hire's job location,
 * then the one picked in the header), else the company's main location,
 * else their only one.
 */
export const defaultEmployeeBranchId = (
  branches: { id: string; isDefault?: boolean }[],
  preferred: (string | null | undefined)[]
): string | undefined => {
  const ids = new Set(branches.map((branch) => branch.id));
  const wanted = preferred.find((id): id is string => !!id && ids.has(id));
  if (wanted) return wanted;
  const main = branches.find((branch) => branch.isDefault);
  if (main) return main.id;
  return branches.length === 1 ? branches[0].id : undefined;
};
